import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Container } from '@/components/ui';
import { ProtectedRoute } from '@/lib/auth/ProtectedRoute';
import { AppShell } from '@/routes/AppShell';
import { Login } from '@/routes/Login';
import { Home } from '@/routes/Home';
import { AuthCallback } from '@/routes/AuthCallback';

// Lazy: rotas pesadas / preview
const DesignSystemPreview = lazy(() => import('@/routes/_design-system'));
const Request = lazy(() => import('@/routes/Request').then((m) => ({ default: m.Request })));
const MyRequests = lazy(() =>
  import('@/routes/MyRequests').then((m) => ({ default: m.MyRequests })),
);
const Approvals = lazy(() => import('@/routes/Approvals').then((m) => ({ default: m.Approvals })));
const Audit = lazy(() => import('@/routes/Audit').then((m) => ({ default: m.Audit })));

// W3+W4 iam-client-portfolio (Clients user-facing deprecated: /clients -> / em UX pass 1)
const ClientDetail = lazy(() =>
  import('@/routes/ClientDetail').then((m) => ({ default: m.ClientDetailRoute })),
);
const AdminClients = lazy(() =>
  import('@/routes/admin/Clients').then((m) => ({ default: m.AdminClients })),
);
const AdminNewClient = lazy(() =>
  import('@/routes/admin/NewClient').then((m) => ({ default: m.NewClient })),
);
const AdminEditClient = lazy(() =>
  import('@/routes/admin/EditClient').then((m) => ({ default: m.EditClient })),
);
const AdminGrants = lazy(() =>
  import('@/routes/admin/Grants').then((m) => ({ default: m.AdminGrants })),
);
const AdminUsers = lazy(() =>
  import('@/routes/admin/Users').then((m) => ({ default: m.AdminUsers })),
);
const AdminBreakGlass = lazy(() =>
  import('@/routes/admin/BreakGlass').then((m) => ({ default: m.AdminBreakGlass })),
);

function LazyShell({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <Container className="py-20">
          <p className="text-muted">Carregando…</p>
        </Container>
      }
    >
      {children}
    </Suspense>
  );
}

function NotFound() {
  return (
    <Container size="md" className="py-20 text-center">
      <p className="text-5xl font-semibold text-ink">404</p>
      <p className="mt-4 text-base text-muted">Página não encontrada.</p>
      <a
        href="/"
        className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
      >
        Voltar ao início
      </a>
    </Container>
  );
}

function Forbidden() {
  return (
    <Container size="md" className="py-20 text-center">
      <p className="text-5xl font-semibold text-ink">403</p>
      <p className="mt-4 text-base text-muted">
        Você está autenticado mas não tem permissão para acessar esta área.
      </p>
      <a
        href="/"
        className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
      >
        Voltar ao início
      </a>
    </Container>
  );
}

export function App() {
  return (
    <Routes>
      {/* Públicas */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forbidden" element={<Forbidden />} />

      {/* Dev preview (sempre aberto para iteração visual) */}
      <Route
        path="/_design-system"
        element={
          <LazyShell>
            <DesignSystemPreview />
          </LazyShell>
        }
      />

      {/* Protegidas (qualquer usuário autenticado) */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Home />} />
        <Route
          path="/request"
          element={
            <LazyShell>
              <Request />
            </LazyShell>
          }
        />
        <Route
          path="/requests"
          element={
            <LazyShell>
              <MyRequests />
            </LazyShell>
          }
        />
        <Route path="/clients" element={<Navigate to="/" replace />} />
        <Route
          path="/clients/:slug"
          element={
            <LazyShell>
              <ClientDetail />
            </LazyShell>
          }
        />
      </Route>

      {/* Protegidas com grupo specific — Aura-TEAMAdmins pode aprovar */}
      <Route
        element={
          <ProtectedRoute requireGroups={['Aura-TEAMAdmins']}>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route
          path="/approvals"
          element={
            <LazyShell>
              <Approvals />
            </LazyShell>
          }
        />
        <Route
          path="/admin/clients"
          element={
            <LazyShell>
              <AdminClients />
            </LazyShell>
          }
        />
        <Route
          path="/admin/clients/new"
          element={
            <LazyShell>
              <AdminNewClient />
            </LazyShell>
          }
        />
        <Route
          path="/admin/clients/:slug/edit"
          element={
            <LazyShell>
              <AdminEditClient />
            </LazyShell>
          }
        />
        <Route
          path="/admin/users"
          element={
            <LazyShell>
              <AdminUsers />
            </LazyShell>
          }
        />
        <Route
          path="/admin/breakglass"
          element={
            <LazyShell>
              <AdminBreakGlass />
            </LazyShell>
          }
        />
      </Route>

      {/* Aura-TEAMAuditors OR Aura-TEAMAdmins — pode ver audit */}
      <Route
        element={
          <ProtectedRoute requireGroups={['Aura-TEAMAuditors', 'Aura-TEAMAdmins']}>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route
          path="/audit"
          element={
            <LazyShell>
              <Audit />
            </LazyShell>
          }
        />
        <Route
          path="/admin/grants"
          element={
            <LazyShell>
              <AdminGrants />
            </LazyShell>
          }
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
